import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save, Play, Trash2, Bookmark, Bell, Loader2 } from "lucide-react";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC","PR"];

export default function SavedSearches() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", q: "", naics: "", state: "", days_back: 30 });
  const [busy, setBusy] = useState(null);
  const [results, setResults] = useState(null); // { search_id, results, new_count }

  const load = () => api.get("/saved-searches").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    try {
      await api.post("/saved-searches", form);
      toast.success("Search saved");
      setForm({ name: "", q: "", naics: "", state: "", days_back: 30 });
      load();
    } catch { toast.error("Failed"); }
  };

  const run = async (id) => {
    setBusy(id);
    try {
      const r = await api.post(`/saved-searches/${id}/run`);
      setResults({ search_id: id, ...r.data });
      toast.success(`${r.data.results.length} results · ${r.data.new_count} new`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Run failed"); }
    finally { setBusy(null); }
  };

  const del = async (id) => {
    await api.del(`/saved-searches/${id}`);
    load();
  };

  const save = async (notice_id) => {
    try {
      await api.post("/opportunities/save", { notice_id });
      setResults(r => r ? { ...r, results: r.results.map(x => x.notice_id === notice_id ? { ...x, saved: true } : x) } : r);
      toast.success("Saved to pipeline");
    } catch { toast.error("Save failed"); }
  };

  return (
    <div className="p-8 stagger-in max-w-6xl" data-testid="saved-searches-page">
      <div className="mb-8">
        <div className="mono-label text-cyan-400 mb-2">// SAVED SEARCHES</div>
        <h1 className="font-display font-black text-4xl tracking-tight">Search Alerts</h1>
        <p className="text-slate-400 mt-2">Save a SAM.gov query and re-run it any time to spot new matches.</p>
      </div>

      <div className="bg-[#0D1424] border border-cyan-400/30 p-5 mb-6 grid md:grid-cols-6 gap-4" data-testid="new-search-form">
        <div className="md:col-span-2">
          <div className="mono-label text-slate-400 mb-2">Name *</div>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. VA roofing 90 days" className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="ss-name" />
        </div>
        <div>
          <div className="mono-label text-slate-400 mb-2">Keyword</div>
          <input value={form.q} onChange={(e) => setForm({ ...form, q: e.target.value })} className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="ss-q" />
        </div>
        <div>
          <div className="mono-label text-slate-400 mb-2">NAICS</div>
          <input value={form.naics} onChange={(e) => setForm({ ...form, naics: e.target.value })} className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="ss-naics" />
        </div>
        <div>
          <div className="mono-label text-slate-400 mb-2">State</div>
          <select value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="ss-state">
            <option value="">Any</option>{US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <div className="mono-label text-slate-400 mb-2">Days</div>
          <select value={form.days_back} onChange={(e) => setForm({ ...form, days_back: Number(e.target.value) })} className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2 text-sm outline-none" data-testid="ss-days">
            <option value="7">7</option><option value="14">14</option><option value="30">30</option><option value="60">60</option><option value="90">90</option>
          </select>
        </div>
        <div className="md:col-span-6">
          <button onClick={create} className="bg-cyan-400 text-[#070B14] px-5 py-2 mono-label hover:bg-cyan-300 flex items-center gap-2" data-testid="ss-create"><Save className="w-4 h-4" />SAVE SEARCH</button>
        </div>
      </div>

      <div className="bg-[#0D1424] border border-white/10 mb-8">
        {items.length === 0 ? <div className="p-10 text-center text-slate-500"><Bell className="w-10 h-10 mx-auto mb-3 text-slate-600" />No saved searches.</div>
          : items.map(s => (
            <div key={s.search_id} className="p-5 border-b border-white/5 flex items-center gap-4" data-testid={`ss-${s.search_id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-white">{s.name}</div>
                <div className="mono-label text-slate-500 mt-1 flex flex-wrap gap-3">
                  {s.q && <span>KEYWORD: {s.q}</span>}
                  {s.naics && <span>NAICS: {s.naics}</span>}
                  {s.state && <span>STATE: {s.state}</span>}
                  <span>LAST {s.days_back}D</span>
                </div>
                {s.last_run_at && <div className="mono-label text-slate-500 mt-1">LAST RUN: {new Date(s.last_run_at).toLocaleString()} · {s.last_result_count} results{s.new_since_last_run > 0 && <span className="text-cyan-400 ml-2">· {s.new_since_last_run} NEW</span>}</div>}
              </div>
              <button onClick={() => run(s.search_id)} disabled={busy === s.search_id} className="bg-cyan-400 text-[#070B14] px-4 py-2 mono-label hover:bg-cyan-300 disabled:opacity-50 flex items-center gap-2" data-testid={`run-${s.search_id}`}>
                {busy === s.search_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}RUN
              </button>
              <button onClick={() => del(s.search_id)} className="text-slate-500 hover:text-red-400 transition-colors p-2" data-testid={`del-${s.search_id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
      </div>

      {results && (
        <div className="bg-[#0D1424] border border-cyan-400/30" data-testid="search-results">
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <div className="mono-label text-cyan-400">RESULTS · {results.results.length} · {results.new_count} NEW</div>
            <button onClick={() => setResults(null)} className="mono-label text-slate-400 hover:text-white" data-testid="close-results">CLOSE</button>
          </div>
          {results.results.map(o => (
            <div key={o.notice_id} className="p-4 border-b border-white/5 flex items-start justify-between gap-4">
              <Link to={`/opportunities/${o.notice_id}`} className="flex-1 min-w-0">
                <div className="mono-label text-slate-500 mb-1">{o.agency} · {o.state}</div>
                <div className="font-medium truncate">{o.title}</div>
              </Link>
              <div className="text-right shrink-0 flex items-center gap-4">
                <div className={`font-display font-black text-2xl ${o.match_score >= 85 ? "text-cyan-400" : "text-amber-400"}`}>{o.match_score}</div>
                {o.saved ? <span className="mono-label text-green-400 flex items-center gap-1"><Bookmark className="w-3 h-3" fill="currentColor" />SAVED</span>
                        : <button onClick={() => save(o.notice_id)} className="mono-label text-cyan-400 hover:text-cyan-300 flex items-center gap-1" data-testid={`save-result-${o.notice_id}`}><Bookmark className="w-3 h-3" />SAVE</button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
